import {
  Box,
  Button,
  Flex,
  Tab,
  Table,
  TableContainer,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { BackLink } from '../../../components/BackLink'
import { LoadingBlock } from '../../../components/Loading'
import { MoneyInput } from '../../../components/MaskedInput'
import { PageTitle } from '../../../components/PageTitle'
import { FormSelect } from '../../../components/Select'
import { useAppToast } from '../../../helpers'
import FinancialIndicatorsService from '../../../services/FinancialIndicators/FinancialIndicatorsService'

const service = new FinancialIndicatorsService()

const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
]

interface IMonthRow {
  month: number
  revenue: number | null
  expenses: number | null
}

interface IGoalRow {
  goal_number: number
  target_value: number | null
}

// ─── Aba 1: Indicadores Financeiros ──────────────────────────────────────────

function IndicadoresTab() {
  const toast = useAppToast()
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [rows, setRows] = useState<IMonthRow[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const years = Array.from({ length: currentYear - 2022 + 1 }, (_, i) => 2022 + i)

  useEffect(() => {
    loadData()
  }, [selectedYear])

  async function loadData() {
    setIsLoading(true)
    try {
      const res = await service.getIndicators(selectedYear, 1, selectedYear, 12)
      const monthlyData = res.data.monthly_data

      const newRows: IMonthRow[] = MONTHS.map((_, idx) => {
        const existing = monthlyData.find((d) => d.month === idx + 1)
        return {
          month: idx + 1,
          revenue: existing ? existing.revenue : null,
          expenses: existing ? existing.expenses : null
        }
      })

      setRows(newRows)
    } catch {
      toast.error('Erro ao carregar dados.')
    } finally {
      setIsLoading(false)
    }
  }

  function updateRow(index: number, field: 'revenue' | 'expenses', value: number | null) {
    const newRows = [...rows]
    newRows[index] = { ...newRows[index], [field]: value }
    setRows(newRows)
  }

  async function handleSave() {
    const dataToSave = rows
      .filter((r) => r.revenue != null || r.expenses != null)
      .map((r) => ({
        year: selectedYear,
        month: r.month,
        revenue: r.revenue ?? 0,
        expenses: r.expenses ?? 0
      }))

    if (dataToSave.length === 0) {
      toast.warning('Nenhum dado para salvar.')
      return
    }

    setIsSaving(true)
    try {
      await service.updateMonthlyData({ data: dataToSave })
      toast.success('Dados salvos com sucesso!')
    } catch {
      toast.error('Erro ao salvar dados.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Box>
      <Flex align="center" gap={3} mb={4}>
        <Text fontWeight="semibold">Ano:</Text>
        <FormSelect
          size="sm"
          w="100px"
          value={selectedYear}
          onChange={(value) => setSelectedYear(Number(value))}
          options={years.map((y) => ({ value: y, label: String(y) }))}
          hideEmptyOption
        />
      </Flex>

      {isLoading ? (
        <LoadingBlock />
      ) : (
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>Mês</Th>
                <Th>Faturamento</Th>
                <Th>Despesas</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row, idx) => (
                <Tr key={row.month}>
                  <Td fontWeight="500">{MONTHS[idx]}</Td>
                  <Td>
                    <MoneyInput size="sm" value={row.revenue} onChange={(value) => updateRow(idx, 'revenue', value)} />
                  </Td>
                  <Td>
                    <MoneyInput
                      size="sm"
                      value={row.expenses}
                      onChange={(value) => updateRow(idx, 'expenses', value)}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      <Flex justify="flex-end" mt={4}>
        <Button colorScheme="magenta" onClick={handleSave} isLoading={isSaving}>
          Salvar
        </Button>
      </Flex>
    </Box>
  )
}

// ─── Aba 2: Metas ─────────────────────────────────────────────────────────────

function MetasTab() {
  const toast = useAppToast()
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [goalRows, setGoalRows] = useState<IGoalRow[]>([
    { goal_number: 1, target_value: null },
    { goal_number: 2, target_value: null },
    { goal_number: 3, target_value: null }
  ])
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const years = Array.from({ length: currentYear - 2022 + 2 }, (_, i) => 2022 + i)

  useEffect(() => {
    loadGoals()
  }, [selectedYear])

  async function loadGoals() {
    setIsLoading(true)
    try {
      const res = await service.getGoals(selectedYear)
      const goals = res.data.goals

      const newRows: IGoalRow[] = [1, 2, 3].map((num) => {
        const existing = goals.find((g) => g.goal_number === num)
        return {
          goal_number: num,
          target_value: existing ? existing.target_value : null
        }
      })

      setGoalRows(newRows)
    } catch {
      toast.error('Erro ao carregar metas.')
    } finally {
      setIsLoading(false)
    }
  }

  function updateGoal(index: number, value: number | null) {
    const newRows = [...goalRows]
    newRows[index] = { ...newRows[index], target_value: value }
    setGoalRows(newRows)
  }

  async function handleSave() {
    const goalsPayload = goalRows.map((r) => ({
      goal_number: r.goal_number,
      target_value: r.target_value ?? 0
    }))

    setIsSaving(true)
    try {
      await service.updateGoals({ year: selectedYear, goals: goalsPayload })
      toast.success('Metas salvas com sucesso!')
    } catch {
      toast.error('Erro ao salvar metas.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Box>
      <Flex align="center" gap={3} mb={4}>
        <Text fontWeight="semibold">Ano:</Text>
        <FormSelect
          size="sm"
          w="100px"
          value={selectedYear}
          onChange={(value) => setSelectedYear(Number(value))}
          options={years.map((y) => ({ value: y, label: String(y) }))}
          hideEmptyOption
        />
      </Flex>

      {isLoading ? (
        <LoadingBlock />
      ) : (
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>Meta</Th>
                <Th>Valor Alvo (Lucro)</Th>
              </Tr>
            </Thead>
            <Tbody>
              {goalRows.map((row, idx) => (
                <Tr key={row.goal_number}>
                  <Td fontWeight="500">Meta {row.goal_number}</Td>
                  <Td>
                    <MoneyInput size="sm" value={row.target_value} onChange={(value) => updateGoal(idx, value)} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      <Text variant="caption" mt={3}>
        A meta é considerada &#34;batida&#34; quando o lucro acumulado do ano atinge ou ultrapassa o valor alvo.
      </Text>

      <Flex justify="flex-end" mt={4}>
        <Button colorScheme="magenta" onClick={handleSave} isLoading={isSaving}>
          Salvar
        </Button>
      </Flex>
    </Box>
  )
}

// ─── Página principal ──────────────────────────────────────────────────────────

export default function FinancialIndicatorsPage() {
  return (
    <>
      <BackLink to="/home" mb={3} />

      <PageTitle title="Indicadores Financeiros" description="Indicadores e metas usados no painel inicial" mb={6} />

      <Tabs>
        <TabList>
          <Tab>Indicadores Financeiros</Tab>
          <Tab>Metas</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <IndicadoresTab />
          </TabPanel>
          <TabPanel>
            <MetasTab />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  )
}
